import type { Renderer2 } from '@angular/core';
import { Injectable, RendererFactory2 } from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import { isDefined } from '@freelancer/utils';
import type { ToastAlertContainerComponentInterface } from './toast-alert-container.types';
import type { ToastAlertComponentInterface } from './toast-alert.types';

@Injectable({ providedIn: 'root' })
export class ToastAlertService {
  private toastAlerts: { [k: string]: ToastAlertComponentInterface } = {};
  private activeToasts: ToastAlertComponentInterface[] = [];
  private containers: ToastAlertContainerComponentInterface[] = [];
  private renderer: Renderer2;

  constructor(rendererFactory: RendererFactory2, private timeUtils: TimeUtils) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  add(toastAlertItem: ToastAlertComponentInterface, id: string) {
    this.toastAlerts = { ...this.toastAlerts, ...{ [id]: toastAlertItem } };
  }

  remove(id: string) {
    delete this.toastAlerts[id];
  }

  registerContainer(
    toastAlertContainer: ToastAlertContainerComponentInterface,
  ) {
    this.containers = [...this.containers, toastAlertContainer];
  }

  unregisterContainer(
    toastAlertContainer: ToastAlertContainerComponentInterface,
  ) {
    this.containers = this.containers.filter(
      item => item !== toastAlertContainer,
    );
  }

  open(id: string) {
    // wait a cycle before opening
    // the container might be rendered after the element
    this.timeUtils.setTimeout(() => {
      const toastAlertItem = this.toastAlerts[id];

      if (!isDefined(toastAlertItem)) {
        throw new Error(`Nonexistent toast alert ${id}`);
      }

      if (this.activeToasts.includes(toastAlertItem)) {
        toastAlertItem.resetTimer();
      } else {
        this.containers.forEach(item => {
          if (item.isElementVisible()) {
            this.renderer.appendChild(
              item.container.nativeElement,
              toastAlertItem.element,
            );
          }
        });

        this.renderer.addClass(toastAlertItem.element, 'IsActive');
        toastAlertItem.toggleVisibility('visible');
        toastAlertItem.startTimer();
        this.addToActiveToasts(toastAlertItem);
      }
    });
  }

  close(id: string) {
    const toastAlertItem = this.toastAlerts[id];

    if (typeof toastAlertItem === 'undefined') {
      return;
    }
    toastAlertItem.toggleVisibility('hidden');
    this.activeToasts = this.activeToasts.filter(x => x.id !== id);
    this.repositionItems();
  }

  addToActiveToasts(item: ToastAlertComponentInterface) {
    this.activeToasts = [...this.activeToasts, item];
    this.repositionItems();
  }

  removeElement(id: string) {
    const toastAlertItem = this.toastAlerts[id];

    if (toastAlertItem) {
      this.containers.forEach(item => {
        if (item.isElementVisible()) {
          this.renderer.removeChild(
            item.container.nativeElement,
            toastAlertItem.element,
          );
        }
      });
    }
  }

  private repositionItems() {
    const offset = 8;
    let position = offset;

    this.activeToasts.forEach(item => {
      item.move(position);
      position += item.element.offsetHeight + offset;
    });
  }
}
